import { Matrix4, Vector3 } from "three";
import { Consts } from "./Consts.js";
import { vec3Pool } from "./ResourcePool/Vector3Pool.js";
import { getPositionFromObject3D, getPositionsFromObject3D, Utils } from "./Utils.js";
export const horDir = new Vector3(1, 0, 0);
export function specularPoint(point, center, target = point) {
    const [_point, _center, _target] = getPositionsFromObject3D([point, center, target]);
    return _target.subVectors(_center, _point).add(_center);
}
export function projectOnLineDir(point, lineDir, linePoint, target = point) {
    const [_point, _linePoint, _target] = getPositionsFromObject3D([point, linePoint, target]);
    return _target.subVectors(_point, _linePoint).projectOnVector(lineDir).add(_linePoint);
}
export function projectOnLinePoints(point, lineA, lineB, target = point) {
    const [_point, _lineA, _lineB, _target] = getPositionsFromObject3D([point, lineA, lineB, target]);
    const lineDir = vec3Pool.get().subVectors(_lineA, _lineB);
    _target.subVectors(_point, _lineA).projectOnVector(lineDir).add(_lineA);
    vec3Pool.release(lineDir);
    return _target;
}
export function perpendicular(dir, target = dir, normal = Consts.planeNormal) {
    target = getPositionFromObject3D(target);
    return target.copy(dir).cross(normal);
}
/**
 * Contains useful vectors functions.
 */
// @dynamic
export class VectorUtils {
    /**
    * Get the nearest point from segment created by sidePoint1 and sidePoint2 .
    */
    static getNearestPointFromSegment(sidePoint1, sidePoint2, ...points) {
        let min = Number.MAX_VALUE;
        let nearestPoint;
        for (const point of points) {
            const distance = point.distanceTo(this.projectOnVector(point, sidePoint1, sidePoint2));
            if (distance < min) {
                min = distance;
                nearestPoint = point;
            }
        }
        return nearestPoint;
    }
    /**
    * Get the nearest point from segment created by sidePoint1 and sidePoint2 .
    */
    static getNearestPoint(distancePoint, ...points) {
        let min = Number.MAX_VALUE;
        let nearestPoint;
        for (const point of points) {
            const distance = point.distanceTo(distancePoint);
            if (distance < min) {
                min = distance;
                nearestPoint = point;
            }
        }
        return nearestPoint;
    }
    /**
     * Checks if point is inside CONVEX polygon. Vertices must be consecuctive.
     */
    static isPointInsidePolygon(point, planeNormal, ...vertices) {
        if (vertices.length > 2) {
            vertices.push(vertices[0], vertices[1]);
            for (let i = 0; i < vertices.length - 2; i++) {
                if (!this.arePointsOnSameSide(vertices[i], vertices[i + 1], planeNormal, point, vertices[i + 2])) {
                    return false;
                }
            }
        }
        return true;
    }
    /**
     * Checks if N points are on the sime side. https://imgur.com/a/ypq4iPd
     */
    static arePointsOnSameSide(sidePoint1, sidePoint2, planeNormal, ...points) {
        const sideNormal = this.getPerpendicular(sidePoint1.clone().sub(sidePoint2), planeNormal);
        if (points.length > 1) {
            const referenceSign = this.computeSign(points[0], sidePoint1, sideNormal);
            for (let i = 1; i < points.length; i++) {
                if (referenceSign !== this.computeSign(points[i], sidePoint1, sideNormal)) {
                    return false;
                }
            }
        }
        return true;
    }
    static invertDirIfOutOfValidArea(dir, sidePoint1, sidePoint2, validPoint) {
        if (!VectorUtils.arePointsOnSameSide(sidePoint1, sidePoint2, Consts.planeNormal, sidePoint1.clone().add(dir), validPoint)) {
            dir.multiplyScalar(-1);
        }
        return dir;
    }
    /**
   * Checks if N points are on the sime side. https://imgur.com/a/ypq4iPd
   */
    static atLeastAPointOnSameSide(sidePoint1, sidePoint2, planeNormal, validPoint, ...points) {
        const sideNormal = this.getPerpendicular(sidePoint1.clone().sub(sidePoint2), planeNormal);
        const indexes = [];
        if (points.length > 0) {
            const referenceSign = this.computeSign(validPoint, sidePoint1, sideNormal);
            for (let i = 0; i < points.length; i++) {
                if (referenceSign === this.computeSign(points[i], sidePoint1, sideNormal)) {
                    indexes.push(i);
                }
            }
        }
        return { result: indexes.length > 0, indexes };
    }
    /**
     * Checks if N points are on the sime side. https://imgur.com/a/ypq4iPd
     */
    static arePointsOnSameSide2(sidePoint, sideNormal, ...points) {
        if (points.length > 1) {
            const referenceSign = this.computeSign(points[0], sidePoint, sideNormal);
            for (let i = 1; i < points.length; i++) {
                if (referenceSign !== this.computeSign(points[i], sidePoint, sideNormal)) {
                    return false;
                }
            }
        }
        return true;
    }
    /**
     * Projects a point on a line.
     */
    static projectOnVector(vec, sidePoint1, sidePoint2) {
        return vec.clone().sub(sidePoint1).projectOnVector(sidePoint1.clone().sub(sidePoint2)).add(sidePoint1);
    }
    /**
     * Calculates the sign of a point, given a sidePoint and its relative normal.
     */
    static computeSign(point, sidePoint, sideNormal) {
        return Math.sign(point.clone().sub(sidePoint).dot(sideNormal));
    }
    /**
     * Checks if two vectors have the same direction.
     */
    static haveSameDirection(v1, v2, tolerance = 0.99) {
        return v1.clone().normalize().dot(v2.clone().normalize()) > tolerance;
    }
    /**
   * Check if two vectors have opposite direction
   */
    static haveOppositeDirection(v1, v2) {
        return v1.clone().normalize().dot(v2.clone().normalize()) < -0.99;
    }
    /**
     * Checks if a point calculated by the intersection of the LINE created by v1 and v2 and an other segment is placed on the SEGMENT created by v1 and v2.
     */
    static isPointOnSegment(v1, v2, intersectionPoint) {
        return !this.haveSameDirection(v1.clone().sub(intersectionPoint), v2.clone().sub(intersectionPoint));
    }
    /**
     * Checks if a point calculated by the intersection of the LINE created by v1 and v2 and an other segment is placed on the SEGMENT created by v1 and v2.
     */
    static isPointOnSegmentWithTolerance(v1, v2, intersectionPoint, decimalsTolerance = 5) {
        const v1s = VectorUtils.fixApproximalVecError(v1.clone().sub(intersectionPoint), decimalsTolerance);
        const v2s = VectorUtils.fixApproximalVecError(v2.clone().sub(intersectionPoint), decimalsTolerance);
        return v1s.length() === 0 || v2s.length() === 0 || !this.haveSameDirection(v1s, v2s);
    }
    /**
     * Gets the bisector of two vectors (doesn't work if v1 and v2 are the same).
     */
    static getBisector(v1, v2) {
        if (this.haveOppositeDirection(v1, v2)) {
            return this.getPerpendicular(v1, Consts.planeNormal);
        }
        return v1.clone().normalize().add(v2.clone().normalize());
    }
    /**
     * Gets the vector perpendicular.
     */
    static getPerpendicular(vec, planeNormal = Consts.planeNormal) {
        return vec.clone().cross(planeNormal);
    }
    /**
     * Gets the perpendicular of a vector creaded by subbing A and B.
     */
    static getPerpendicularByPoints(A, B, planeNormal = Consts.planeNormal) {
        return A.clone().sub(B).cross(planeNormal);
    }
    /**
     * Gets the vector perpendicular with the direction based on a side point.
     */
    static getPerpendicularWithDirectionBasedOnPoint(sidePoint1, sidePoint2, referencePoint, planeNormal = Consts.planeNormal) {
        const sideNormalPerpendicular = this.getPerpendicular(sidePoint1.clone().sub(sidePoint2), planeNormal).setLength(1);
        this.invertDirIfOutOfValidArea(sideNormalPerpendicular, sidePoint1, sidePoint2, referencePoint);
        return sideNormalPerpendicular;
    }
    /**
     * Checks if two vectors are equals and both initialized. It doesn't check the pointer but the values.
     */
    static areVec3EqualsWithNullCheck(v1, v2) {
        return v1 && v2 && v1.equals(v2);
    }
    /**
     * Checks if two vectors are equals and both initialized. It doesn't check the pointer but the values.
     */
    static areVec3EqualsWithTolerance(v1, v2, tolerance = 10 ** -3) {
        return Math.abs(v1.x - v2.x) < tolerance && Math.abs(v1.y - v2.y) < tolerance && Math.abs(v1.z - v2.z) < tolerance;
    }
    /**
     * Checks if a vector is empty (all values are 0).
     */
    static isVec3Empty(v) {
        return v.x === 0 && v.y === 0 && v.z === 0;
    }
    /**
     * Checks that two euler are equals and both initialized. It doesn't check the pointer but the values.
     */
    static areEulerEqualsWithNullCheck(e1, e2) {
        return e1 && e2 && e1.equals(e2);
    }
    /**
     * Computes intersection of two lines considering just xy components. TODO refactor
     */
    static lines2DIntersection(L1A, L1B, L2A, L2B, tolerance = 3) {
        if (L1A.equals(L1B) || L2A.equals(L2B)) {
            return undefined;
        }
        L1A.z !== 0 && (L1A = L1A.clone().setZ(0));
        L1B.z !== 0 && (L1B = L1B.clone().setZ(0));
        L2A.z !== 0 && (L2A = L2A.clone().setZ(0));
        L2B.z !== 0 && (L2B = L2B.clone().setZ(0));
        //rimozione casi di sghembe e parallele
        const det = new Matrix4();
        det.set(L1A.x, L2A.x, L1B.x, L2B.x, L1A.y, L2A.y, L1B.y, L2B.y, L1A.z, L2A.z, L1B.z, L2B.z, 1, 1, 1, 1);
        //check skewness (sghembe)
        if (det.determinant() > 10 ** -tolerance) {
            return undefined;
        }
        //check parallelism
        const L1BL1A = L1B.clone().sub(L1A);
        const L2BL2A = L2B.clone().sub(L2A);
        const angle = L1BL1A.angleTo(L2BL2A);
        if (Utils.numberRound(angle, tolerance) === 0 || Utils.numberRound(Math.abs((Math.PI - angle)), tolerance) === 0) {
            // rette parallele
            return undefined;
        }
        const leastSquareVector = new Vector3(L2A.x - L1A.x, L2A.y - L1A.y, L2A.z - L1A.z);
        const a = L1BL1A.lengthSq(), b = L1BL1A.clone().dot(L2BL2A), c = L1BL1A.clone().dot(L2BL2A), d = L2BL2A.lengthSq();
        const det2 = a * d - b * c;
        const Atb1 = L1BL1A.clone().dot(leastSquareVector);
        const Atb2 = L2BL2A.clone().dot(leastSquareVector);
        const first = (Atb1 * d - Atb2 * b) / det2;
        return new Vector3(L1A.x + first * L1BL1A.x, L1A.y + first * L1BL1A.y, L1A.z + first * L1BL1A.z);
    }
    /**
     * Gets the 2d angle with sign between A - origin and B - origin.
     */
    static linesAngleFromOrigin(a, b) {
        const v1 = a.clone().setZ(0).normalize();
        const v2 = b.clone().setZ(0).normalize();
        const angle = Math.acos(v1.dot(v2));
        return isNaN(angle) ? 0 : (v1.clone().cross(v2).dot(Consts.planeNormal) >= 0 ? angle : -angle);
    }
    /**
    * Gets the 2d angle with sign between A and B. Works only with absolute reference system.
    */
    static linesAngle(a, b, center) {
        const v1 = a.clone().sub(center).setZ(0).normalize();
        const v2 = b.clone().sub(center).setZ(0).normalize();
        const angle = Math.acos(v1.dot(v2));
        return isNaN(angle) ? 0 : (v1.clone().cross(v2).dot(Consts.planeNormal) >= 0 ? angle : -angle);
    }
    /**
     * Transforms an angle interval (-math.PI to math.Pi) to (0 to 2 Math.PI).
     */
    static getAngleTo2PI(angle) {
        return angle < 0 ? angle + 2 * Math.PI : angle;
    }
    /**
    * Converts object3D local vector to the world coordinates system.
    */
    static getLocalToWorldOrientation(obj, pos) {
        return pos.clone().applyMatrix4(obj.matrixWorld.clone().setPosition(0, 0, 0));
    }
    /**
     * Converts world vector to the object3D local coordinates system.
     */
    static getWorldToLocalOrientation(obj, pos) {
        return pos.clone().applyMatrix4(obj.matrixWorld.clone().setPosition(0, 0, 0).transpose()); //todo remove scaling?
    }
    /**
     * Gets center of mesh bouding box.
     */
    static getStaticGeometryCenter(mesh) {
        !mesh.geometry.boundingBox && mesh.geometry.computeBoundingBox();
        return mesh.geometry.boundingBox.getCenter(new Vector3());
    }
    /**
     * Gets mesh center position in the world space.
     */
    static getWorldCenter(mesh) {
        return mesh.localToWorld(this.getStaticGeometryCenter(mesh));
    }
    /**
     * Gets mesh center in the woorld coordinates system.
     */
    static getCenterWorldOrientation(mesh) {
        return this.getLocalToWorldOrientation(mesh, this.getStaticGeometryCenter(mesh));
    }
    /**
     * Checks if two segments are touching.
     */
    static areSegmentTouching(x1, x2, y1, y2) {
        const p = this.lines2DIntersection(x1, x2, y1, y2);
        return p !== undefined && this.isPointOnSegment(x1, x2, p) && this.isPointOnSegment(y1, y2, p);
    }
    /**
     * Gets the center of the polygon obtained by vertices.
     */
    static getVerticesCenter(...vertices) {
        const sum = new Vector3();
        for (const vertex of vertices) {
            sum.add(vertex);
        }
        return sum.divideScalar(vertices.length);
    }
    /**
     * Converts 1.0000000001 to 1 or 1.9999999999 to 2
     */
    static fixApproximalError(value, minDecimals = 10) {
        const eps = 10 ** -minDecimals;
        const sign = Math.sign(value);
        const valueToFix = Math.abs(value % 1);
        if (valueToFix < eps) {
            return Math.floor(Math.abs(value)) * sign;
        }
        if (valueToFix > 1 - eps) {
            return Math.ceil(Math.abs(value)) * sign;
        }
        return value;
    }
    /**
    * Converts a vec with components 1.0000000001 to 1 or 1.9999999999 to 2
    */
    static fixApproximalVecError(vec, minDecimals = 5) {
        vec.x = this.fixApproximalError(vec.x, minDecimals);
        vec.y = this.fixApproximalError(vec.y, minDecimals);
        vec.z = this.fixApproximalError(vec.z, minDecimals);
        return vec;
    }
    /**
     * Sorts vertices of a polygon in clockwise order.
     */
    static sortConvexPolygonVertices(vertices) {
        const center = VectorUtils.getVerticesCenter(...vertices);
        const occ = [];
        for (let i = 1; i < vertices.length; i++) {
            occ.push({ angle: VectorUtils.getAngleTo2PI(VectorUtils.linesAngle(vertices[0], vertices[i], center)), index: i });
        }
        occ.sort((x, y) => x.angle - y.angle);
        const sortedVertices = [vertices[0]];
        for (let i = 0; i < vertices.length - 1; i++) {
            sortedVertices.push(vertices[occ[i].index]);
        }
        return sortedVertices;
    }
    /**
     * Check if two values are equals considering a tolerance to avoid aproximal errors.
     */
    static areEqualsWithTolerance(x, y, tolerance = 10 ** -6) {
        return Math.abs(x - y) <= tolerance;
    }
}
//# sourceMappingURL=VectorUtils.js.map